import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext();
const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'rideburaq_token';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const authHeaders = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const parseError = async (res, fallback) => {
  try {
    const data = await res.json();
    const detail = data?.detail;
    if (typeof detail === 'string') return detail;
    if (detail && typeof detail.message === 'string') return detail.message;      // {code, message} answers (e.g. the sign-in lockout)
    if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  } catch (e) {
    console.debug('parseError: could not parse error body', e);
  }
  return fallback;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchRides = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/rides`, { headers: { ...authHeaders() } });
      if (res.ok) {
        const list = await res.json();
        setRides(Array.isArray(list) ? list : []);          // never anything but a list
      }
    } catch (e) {
      console.debug('fetchRides failed', e);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        const res = await fetch(`${API}/api/auth/me`, { headers: { ...authHeaders() } });
        if (res.ok) {
          setCurrentUser(await res.json());
          await fetchRides();
        } else {
          localStorage.removeItem(TOKEN_KEY);
        }
      } catch (e) {
        console.debug('auth init failed', e);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [fetchRides]);

  const signup = async (userData) => {
    const res = await fetch(`${API}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData),
    });
    if (!res.ok) {
      throw new Error(await parseError(res, 'Signup failed. Please try again.'));
    }
    const data = await res.json();
    localStorage.setItem(TOKEN_KEY, data.access_token);
    setCurrentUser(data.user);
    setRides([]);
    return data.user;
  };

  const login = async (email, password) => {
    const res = await fetch(`${API}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      throw new Error(await parseError(res, 'Invalid email or password.'));
    }
    const data = await res.json();
    localStorage.setItem(TOKEN_KEY, data.access_token);
    setCurrentUser(data.user);
    await fetchRides();
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setCurrentUser(null);
    setRides([]);
  };

  const appleLogin = async (payload) => {
    const res = await fetch(`${API}/api/auth/apple/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...payload, platform: 'web' }),
    });
    if (!res.ok) {
      throw new Error(await parseError(res, 'Apple sign-in failed. Please try again.'));
    }
    const data = await res.json();
    localStorage.setItem(TOKEN_KEY, data.access_token);
    setCurrentUser(data.user);
    await fetchRides();
    return data.user;
  };

  const updatePaymentStatus = async (hasPayment, paymentMethod = null) => {
    const res = await fetch(`${API}/api/auth/me/payment`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ hasPayment, paymentMethod }),
    });
    if (res.ok) {
      setCurrentUser(await res.json());
    }
  };

  const bookRide = async (rideData) => {
    const res = await fetch(`${API}/api/rides`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(rideData),
    });
    if (!res.ok) {
      throw new Error(await parseError(res, 'Failed to book ride.'));
    }
    const ride = await res.json();
    await fetchRides();
    return ride;
  };

  const getUserRides = useCallback(() => rides, [rides]);

  const addTip = async (rideId, tipAmount, tipPercent = null) => {
    const res = await fetch(`${API}/api/rides/${rideId}/tip`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ tipAmount, tipPercent }),
    });
    if (!res.ok) {
      throw new Error(await parseError(res, 'Failed to add gratuity.'));
    }
    await fetchRides();
    return res.json();
  };

  const toggleFavoriteDriver = async (driver) => {
    const res = await fetch(`${API}/api/auth/me/favorite-drivers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ driver }),
    });
    if (!res.ok) {
      throw new Error(await parseError(res, 'Failed to update favorites.'));
    }
    const user = await res.json();
    setCurrentUser(user);
    return user;
  };

  const value = {
    currentUser,
    rides,
    signup,
    login,
    logout,
    appleLogin,
    updatePaymentStatus,
    bookRide,
    getUserRides,
    addTip,
    toggleFavoriteDriver,
    refreshRides: fetchRides,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
